'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { manageCart, updateCartBuyerIdentity } from '@/lib/shopify';

interface CartItem {
  id: string;
  quantity: number;
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
  };
  merchandise: {
    id: string;
    title: string;
    price: {
      amount: string;
      currencyCode: string;
    };
    image?: {
      url: string;
      altText: string;
      width: number;
      height: number;
    };
    product: {
      title: string;
      handle: string;
      vendor: string;
      featuredImage?: {
        url: string;
        altText: string;
        width: number;
        height: number;
      };
    };
  };
}

interface CartCost {
  totalAmount: {
    amount: string;
    currencyCode: string;
  };
  subtotalAmount: {
    amount: string;
    currencyCode: string;
  };
}

interface CartContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  cartId: string | null;
  items: CartItem[];
  totalQuantity: number;
  cost: CartCost | null;
  checkoutUrl: string | null;
  addToCart: (variantId: string, quantity: number) => Promise<void>;
  updateQuantity: (lineId: string, quantity: number) => Promise<void>;
  removeItem: (lineId: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function getBuyerAccessToken(): string | undefined {
  try {
    const raw = localStorage.getItem("shopifyUserToken");
    if (!raw) return;
    return JSON.parse(raw).accessToken;
  } catch {
    return;
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [cartId, setCartId] = useState<string | null>(null);
  const [totalQuantity, setTotalQuantity] = useState(0);
  const [cost, setCost] = useState<CartCost | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<CartItem[]>([]);

  useEffect(() => {
    const savedCartId = localStorage.getItem('cartId');
    const token = getBuyerAccessToken();
  
    if (savedCartId) {
      setCartId(savedCartId);
  
      manageCart(savedCartId, [], token)
        .then(result => {
          updateCartState(result.cart);
        })
        .catch(console.error);
    }
  }, []);

  const updateCartState = (cart: any) => {
    setCartId(cart.id);
    localStorage.setItem('cartId', cart.id);
    const cartItems = cart.lines.edges.map((edge: any) => edge.node);
    setItems(cartItems);
    setTotalQuantity(cart.totalQuantity);
    setCost(cart.cost);
    setCheckoutUrl(cart.checkoutUrl);
  };

  const addToCart = async (merchandiseId: string, quantity: number) => {
    setLoading(true);
    setError(null);
    try {
      // Was this a new cart?
      const wasNewCart = !cartId;
      const accessToken = getBuyerAccessToken();
  
      // Add to cart (may create new cart)
      const result = await manageCart(cartId, [{ merchandiseId, quantity }], accessToken);
      updateCartState(result.cart);
      console.log('manageCart result:', result.cart);
  
      // If a new cart was created and user is logged in, associate the cart with customer
      if (wasNewCart && accessToken) {
        try {
          console.log('Calling updateCartBuyerIdentity with', result.cart.id, accessToken);
          const identityResult = await updateCartBuyerIdentity(result.cart.id, accessToken);
          console.log('Buyer identity update result:', identityResult);
  
          // VERY IMPORTANT: Refresh cart to get the correct, updated checkoutUrl (some stores require this)
          if (identityResult?.id) {
            // Refetch cart state after associating the customer
            const refreshed = await manageCart(identityResult.id, [], accessToken);
            updateCartState(refreshed.cart);
            console.log('Refetched cart after buyer identity update:', refreshed.cart);
          }
        } catch (e) {
          console.error("Failed to associate cart with customer:", e);
        }
      }
    } catch (err: any) {
      console.error('Add to cart error:', err);
      setError(err.message || 'Add to cart failed');
    } finally {
      setLoading(false);
    }
  };
  
  
  const updateQuantity = async (lineId: string, quantity: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await manageCart(cartId, [{ id: lineId, quantity }], getBuyerAccessToken());
      updateCartState(result.cart);
    } catch (err: any) {
      console.error('Update quantity error:', err);
      setError(err.message || 'Update quantity failed');
    } finally {
      setLoading(false);
    }
  };
  
  const removeItem = async (lineId: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await manageCart(cartId, [{ id: lineId, quantity: 0 }], getBuyerAccessToken());

      updateCartState(result.cart);
    } catch (err: any) {
      console.error('Remove item error:', err);
      setError(err.message || 'Remove item failed');
    } finally {
      setLoading(false);
    }
  };

  

  return (
    <CartContext.Provider
      value={{
        isOpen,
        setIsOpen,
        cartId,
        items,
        totalQuantity,
        cost,
        checkoutUrl,
        addToCart,
        updateQuantity,
        removeItem,
        loading,
        error,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}